import React, { useState } from 'react';
import { api } from '../../services/api';
import { useLang } from '../../i18n/context';

interface AddEventModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export function AddEventModal({ onClose, onCreated }: AddEventModalProps) {
  const { t } = useLang();
  const [form, setForm] = useState({
    title: '',
    date: '',
    venue_name: '',
    venue_city: '',
    price_info: '',
    is_free: false,
    description: '',
    genre_tags: [] as string[],
    source_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const GENRE_OPTIONS = ['dub', 'reggae', 'roots', 'dancehall', 'sound system', 'steppers'];

  function toggleGenre(genre: string) {
    setForm(prev => ({
      ...prev,
      genre_tags: prev.genre_tags.includes(genre)
        ? prev.genre_tags.filter(g => g !== genre)
        : [...prev.genre_tags, genre],
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.date) return;

    setSubmitError('');
    setSaving(true);
    try {
      await api.createEvent({
        ...form,
        status: 'validated',
      });
      onCreated();
      onClose();
    } catch (err: any) {
      const msg = String(err?.message || '');
      setSubmitError(
        /auth|401|expir/i.test(msg) ? t('flyer.session_expired') : (msg || t('flyer.save_failed'))
      );
      console.error('Failed to create event:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t('add_event.title')}</div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('add_event.title_label')} *</label>
            <input className="form-input" required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder={t('add_event.event_name')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">{t('add_event.date_label')} *</label>
              <input className="form-input" type="date" required value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.price_label')}</label>
              <input className="form-input" value={form.price_info} onChange={e => setForm(p => ({ ...p, price_info: e.target.value }))} placeholder={t('add_event.price_placeholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.venue_label')}</label>
              <input className="form-input" value={form.venue_name} onChange={e => setForm(p => ({ ...p, venue_name: e.target.value }))} placeholder={t('add_event.venue_placeholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.city_label')}</label>
              <input className="form-input" value={form.venue_city} onChange={e => setForm(p => ({ ...p, venue_city: e.target.value }))} placeholder={t('add_event.city_placeholder')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('add_event.genres_label')}</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {GENRE_OPTIONS.map(g => (
                <button key={g} type="button"
                  className={`filter-chip ${form.genre_tags.includes(g) ? 'active' : ''}`}
                  onClick={() => toggleGenre(g)}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('add_event.description_label')}</label>
            <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t('add_event.description_placeholder')} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('add_event.source_label')}</label>
            <input className="form-input" value={form.source_url} onChange={e => setForm(p => ({ ...p, source_url: e.target.value }))} placeholder="https://..." />
          </div>
          {submitError && (
            <div style={{ marginTop: '12px', padding: '8px 12px', borderRadius: '4px', background: 'rgba(229,57,53,0.12)', border: '1px solid var(--red)', fontSize: '12px', color: 'var(--red)', textAlign: 'center' }}>
              {submitError}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-success" disabled={saving}>
              {saving ? t('add_event.saving') : t('add_event.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
